import { FC, useContext, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { ITabOption, Table, Tabs } from '@smartshell/ui-kit';
import { Column } from 'react-table';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import {
  GET_WORKSHIFT_PAYMENTS,
  IGetWorkShiftPaymentsResponse,
} from './schema';
import s from './SoldItemsBlock.module.scss';

type TSoldItem = {
  title: string;
  amount: number;
  sum: number;
  minute: boolean;
};

type TTab = 'GOOD' | 'SERVICE' | 'TARIFF';

export const SoldItemsBlock: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const TABS: Array<ITabOption<TTab>> = [
    { title: t('soldBlock.tab.goods'), value: 'GOOD' },
    { title: t('soldBlock.tab.tariffs'), value: 'TARIFF' },
    { title: t('soldBlock.tab.services'), value: 'SERVICE' },
  ];

  const PLACEHOLDERS_MAP: Record<TTab, string> = {
    GOOD: t('soldBlock.placeholder.goods'),
    TARIFF: t('soldBlock.placeholder.tariffs'),
    SERVICE: t('soldBlock.placeholder.services'),
  };

  const COLUMNS: Array<Column<TSoldItem>> = useMemo(
    () => [
      {
        Header: t('title'),
        accessor: 'title',
      },
      {
        Header: t('amount'),
        accessor: 'amount',
        Cell: ({
          value,
          row: {
            original: { minute },
          },
        }) =>
          `${value.toLocaleString()} ${t(
            minute ? 'contracted.minutes' : 'pcs'
          )}`,
      },
      {
        Header: t('cost'),
        accessor: 'sum',
        Cell: ({ value }) => `${value.toLocaleString()} ${currency.sign}`,
      },
    ],
    [t]
  );

  const [tab, setTab] = useState<TTab>('GOOD');

  const { data, loading } = useQuery<IGetWorkShiftPaymentsResponse>(
    GET_WORKSHIFT_PAYMENTS,
    {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'ignore',
      skip: !permissions?.has('work_shift_active'),
    }
  );

  const payments = data?.activeWorkShift?.payments ?? [];

  const accumulatedSoldItems = useMemo(() => {
    return (
      payments
        .filter((payment) => !payment?.is_refunded)
        .flatMap((payment) => payment?.items)
        .reduce<Record<TTab, Record<string, TSoldItem>>>(
          (acc, item) => {
            if (
              item?.type !== 'SERVICE' &&
              item?.type !== 'GOOD' &&
              item?.type !== 'TARIFF'
            ) {
              return acc;
            }

            const accumulatedItem = acc[item?.type][item?.entity?.id];

            if (accumulatedItem) {
              return {
                ...acc,
                [item?.type]: {
                  ...acc[item?.type],
                  [item?.entity?.id]: {
                    ...accumulatedItem,
                    sum: accumulatedItem?.sum + item?.sum,
                    amount: accumulatedItem?.amount + item?.amount,
                  },
                },
              };
            }

            return {
              ...acc,
              [item?.type]: {
                ...acc[item?.type],
                [item?.entity?.id]: {
                  title: item?.entity.title,
                  sum: item?.sum,
                  amount: item?.amount,
                  minute: item?.type === 'TARIFF' && item?.entity.per_minute,
                },
              },
            };
          },
          { GOOD: {}, SERVICE: {}, TARIFF: {} }
        ) ?? { GOOD: {}, SERVICE: {}, TARIFF: {} }
    );
  }, [payments]);

  const tableData = Object.values(accumulatedSoldItems[tab]);

  return (
    <Table
      headerBlock={
        <Tabs options={TABS} value={tab} onChange={setTab} className={s.tabs} />
      }
      columns={COLUMNS}
      data={tableData}
      emptyDataPlaceholder={PLACEHOLDERS_MAP[tab]}
      isLoading={loading}
      className={s.table}
    />
  );
};
